<template>
  <div
    :id="uid"
    v-click-outside="fold"
    class="tw-relative tw-w-full tw-outline-none"
    :class="{ disabled, error: !validity, noValue: !selectedLabel, isUnfolded }"
    data-test-id="VSelect"
    tabindex="0"
    @focus="setupKeystrokeListener"
    @blur="deleteKeystrokeListener"
  >
    <VLabel v-if="$slots.label || label" class="tw-mb-2">
      <template #default>
        <slot name="label">{{ label }}</slot>
      </template>
    </VLabel>
    <!-- TODO: turn this into a <select> element and change the label into an actual <label> using LabelInput -->
    <!-- "selectInput" class required for deep stylings -->
    <div
      class="selectInput tw-relative tw-flex tw-h-12 tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-xs tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-p-4 focus-visible:tw-outline focus-visible:tw-outline-offset-2 disabled:tw-pointer-events-none disabled:tw-bg-gray-300 disabled:tw-opacity-75"
      data-test-id="toggleFold"
      :class="{ disabled }"
      tabindex="0"
      @click="toggleFold"
      @keydown.space.prevent.self="toggleFold"
    >
      <span v-if="selectedLabel" class="ellipsis">
        <span class="tw-text-body tw-align-middle">{{ selectedLabel }}</span>
      </span>
      <span v-else-if="placeholder" class="ellipsis tw-align-middle">
        <span class="tw-text-body tw-align-middle tw-text-discrete2">
          {{ placeholder }}
        </span>
      </span>
      <transition name="scale" mode="out-in">
        <UIIcon
          v-if="isUnfolded"
          key="up"
          name="fa6-solid:chevron-up"
          size="sm"
          class="tw-text-icon-discrete2"
        />
        <button
          v-else-if="modelValue && withReset"
          key="reset"
          type="button"
          tabindex="-1"
          @click.stop="resetValue"
        >
          <UIIcon
            name="fa-solid:times"
            size="sm"
            class="tw-text-icon-discrete2"
          />
        </button>
        <UIIcon
          v-else
          key="down"
          name="fa6-solid:chevron-down"
          size="sm"
          class="tw-text-icon-discrete2"
        />
      </transition>
    </div>
    <transition name="fade" mode="out-in">
      <div
        v-if="isUnfolded"
        class="optionsWrapper tw-absolute tw-top-[calc(100%+8px)] tw-z-[999] tw-max-h-[240px] tw-w-full tw-cursor-pointer tw-overflow-y-auto tw-rounded-xs tw-bg-white tw-p-0 tw-shadow"
      >
        <div
          v-for="(option, index) in options"
          :id="`Vselect_${option.label}_${index}`"
          :key="index"
          class="optionElem tw-p-3 tw-pl-4 tw-transition tw-duration-150 tw-ease-in hover:tw-bg-gray-200 hover:tw-duration-100 hover:tw-ease-out"
          data-test-id="option"
          tabindex="0"
          @click.stop="handleOptionClick(option)"
          @keydown.space.prevent.stop="handleOptionClick(option)"
          @keydown.enter.prevent.stop="handleOptionClick(option)"
        >
          <span class="tw-text-body tw-text-default-color">{{
            option.label
          }}</span>
        </div>
      </div>
    </transition>
    <HeightExpand>
      <div
        v-if="$slots['error-message'] && !validity"
        class="tw-mt-1 tw-text-sm tw-text-error"
      >
        <slot name="error-message" />
      </div>
    </HeightExpand>
  </div>
</template>

<script lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { defineComponent } from 'vue'

import { vClickOutside } from '~/directives/ClickOutside'

import HeightExpand from '~/components/transitions/heightExpand.vue'
import VLabel from '~/components/ui/VLabel.vue'

import validationChildMixin from '~/mixins/validationChild'

import getDereferencedValue from '~/helpers/getDereferencedValue'

import type { PropType } from 'vue'

interface Option {
  label: string
  value: number | string
}
type Data = number | string | Record<string, any>

export default defineComponent({
  components: {
    HeightExpand,
    VLabel,
    UIIcon,
  },
  directives: {
    clickOutside: vClickOutside,
  },
  mixins: [validationChildMixin],
  props: {
    available: {
      type: Array as PropType<Data[]>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    labelKey: {
      type: [Array, String] as PropType<string[] | string>,
      default(): string[] {
        return []
      },
    },
    placeholder: {
      type: String,
      default: '',
    },
    transformLabelFn: {
      type: Function as PropType<(value: string) => string>,
      default: (value: string): string => {
        return value
      },
    },
    modelValue: {
      type: [String, Number, null] as PropType<number | string | null>,
      validator(): boolean {
        return true
      },
      default(): null {
        return null
      },
    },
    valueKey: {
      type: [Array, String] as PropType<string[] | string>,
      default(): string[] {
        return []
      },
    },
    withReset: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'change'],
  setup() {
    const uid = useId()

    return {
      uid,
    }
  },
  data() {
    return {
      isUnfolded: false,
      hasKeystrokesWatcher: false,
      keystrokeRecordBuffer: [] as string[],
      keystrokeRecordTimer: 1500,
      keystrokeRecordPurgeTimeout: null as null | number,
    }
  },
  computed: {
    selectedLabel(): string | undefined {
      if (this.modelValue !== null) {
        return this.options.find(
          (option: Option): boolean => option.value === this.modelValue,
        )?.label
      }

      return undefined
    },
    options(): Option[] {
      return this.available.map((element: Data) => this.createOption(element))
    },
    requiredDereferencing(): boolean {
      return (this.valueKey?.length ?? 0) > 0
    },
  },
  methods: {
    setupKeystrokeListener(): void {
      if (!this.hasKeystrokesWatcher && this.isUnfolded) {
        window.addEventListener('keydown', this.keyStrokeController)
        this.hasKeystrokesWatcher = true
      }
    },
    deleteKeystrokeListener(): void {
      window.removeEventListener('keydown', this.keyStrokeController)
      this.hasKeystrokesWatcher = false
    },
    keyStrokeController(e: KeyboardEvent): void {
      if (e.key.length === 1) {
        this.keystrokeRecordBuffer.push(e.key)
        this.keystrokeRecordComparator()
      }
      this.keyStrokePurgeTimer()
      this.keystrokeRecordPurgeTimeout = window.setTimeout(() => {
        this.keystrokeRecordBuffer.splice(0, this.keystrokeRecordBuffer.length)
      }, this.keystrokeRecordTimer)
    },
    keyStrokePurgeTimer(): void {
      if (this.keystrokeRecordPurgeTimeout !== null)
        window.clearTimeout(this.keystrokeRecordPurgeTimeout)
    },
    keystrokeRecordComparator(): void {
      const firstMatchIndex = this.options.findIndex((option) => {
        return this.keystrokeRecordBuffer.every(
          (letter, index) =>
            option.label.toLowerCase()[index] === letter.toLowerCase(),
        )
      })

      if (firstMatchIndex !== -1) {
        const firstMatch = this.options[firstMatchIndex]
        const targetId = `Vselect_${firstMatch.label}_${firstMatchIndex}`

        document.getElementById(targetId)?.scrollIntoView()
      } else {
        this.keyStrokePurgeTimer()
        this.keystrokeRecordBuffer.splice(0, this.keystrokeRecordBuffer.length)
      }
    },
    handleOptionClick(option: Option): void {
      this.$emit('update:modelValue', option.value)
      if (option.value !== this.modelValue) this.$emit('change')

      this.fold()
    },
    createOption(element: Data): Option {
      return {
        label: this.getLabel(element),
        value: this.getValue(element),
      }
    },
    getLabel(element: Data): string {
      let value: string
      if (this.requiredDereferencing && this.labelKey?.length > 0) {
        value =
          getDereferencedValue(this.labelKey, element as Record<string, any>) ??
          ''
      } else {
        value = this.getValue(element)?.toString() ?? ''
      }

      return this.transformLabelFn(value)
    },
    getValue(element: Data): string | number {
      if (this.requiredDereferencing) {
        return getDereferencedValue(
          this.valueKey,
          element as Record<string, any>,
        )
      } else {
        return element as string | number
      }
    },
    toggleFold(): void {
      if (this.disabled) return

      this.isUnfolded = !this.isUnfolded
      if (this.isUnfolded) this.setupKeystrokeListener()
      else this.deleteKeystrokeListener()
    },
    fold(): void {
      this.isUnfolded = false
      this.deleteKeystrokeListener()
    },
    resetValue(): void {
      this.$emit('update:modelValue', null)
    },
  },
})
</script>

<style lang="scss" scoped>
.error > .selectInput {
  @apply tw-border-error;
}

.noValue .selectInput > span {
  @apply tw-text-gray-500;
}
</style>
